import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Upload as UploadIcon, FileText, X, GraduationCap, Check, Loader2, SkipForward, PenTool, Package } from 'lucide-react';
import HandwrittenUpload from '../components/HandwrittenUpload';
import BulkUpload from './BulkUpload';

const FIELDS = ['Computer Science', 'Biology', 'Chemistry', 'Physics', 'Mathematics', 'Engineering', 'Medicine', 'Business', 'Psychology', 'Literature', 'History'];
const DOC_TYPES = ['notes', 'research'];

const Upload = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    field: FIELDS[0],
    price: '',
    preview_content: '',
    full_content: '',
    document_type: DOC_TYPES[0],
    tags: ''
  });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [customField, setCustomField] = useState('');
  const [showCustomField, setShowCustomField] = useState(false);
  const [customDocType, setCustomDocType] = useState('');
  const [showCustomDocType, setShowCustomDocType] = useState(false);
  const [uploadTab, setUploadTab] = useState('document');

  // Post-upload state
  const [uploadStep, setUploadStep] = useState('form'); // 'form' | 'study-dashboard-offer'
  const [uploadedDocId, setUploadedDocId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const tags = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('field', formData.field);
      submitData.append('price', parseFloat(formData.price));
      submitData.append('preview_content', formData.preview_content);
      submitData.append('full_content', formData.full_content);
      submitData.append('document_type', formData.document_type);
      submitData.append('tags', tags.join(','));
      
      if (uploadedFile) {
        submitData.append('file', uploadedFile);
      }
      
      const response = await axios.post(`${API}/documents/upload`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const docId = response.data.id;
      setUploadedDocId(docId);
      toast.success('Document uploaded successfully!');
      setUploadStep('study-dashboard-offer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
  };

  const handleGenerateStudyDashboard = () => {
    navigate(`/study-builder?highlight=${uploadedDocId}`);
  };

  const handleSkipStudyDashboard = () => {
    toast.success('Document uploaded successfully! You can generate a study dashboard anytime from the AI Study Builder.');
    navigate('/');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setUploadedFile(file);

    // Read text files directly for preview
    if (file.type === 'text/plain' || fileExtension === '.txt' || fileExtension === '.md') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        setFormData(prev => ({ ...prev, full_content: content }));
        toast.success('Text file content loaded successfully!');
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
      };
      reader.readAsText(file);
    } else {
      // For all other files, they will be uploaded and available after purchase
      const fileType = file.type.startsWith('image/') ? 'Image' : 
                       file.type.startsWith('video/') ? 'Video' :
                       file.type.startsWith('audio/') ? 'Audio' :
                       fileExtension.match(/\.(pdf|doc|docx)$/i) ? 'Document' : 'File';
      
      toast.success(`${file.name} ready to upload! ${fileType} will be available after purchase.`);
      setFormData(prev => ({ 
        ...prev, 
        full_content: `${fileType}: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)\nThis file will be available for viewing/download after purchase.`
      }));
    }

    // Auto-fill title if empty
    if (!formData.title) {
      const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
      setFormData(prev => ({ ...prev, title: fileName }));
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Study Dashboard Offer Step */}
        {uploadStep === 'study-dashboard-offer' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                Generate a Study Dashboard?
              </h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                AI will create a complete study toolkit — summaries, flashcards, key terms, and quizzes from your document.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8 max-w-lg mx-auto">
                {[
                  { icon: FileText, label: 'Summary' },
                  { icon: GraduationCap, label: 'Key Terms' },
                  { icon: Check, label: 'Flashcards' },
                  { icon: Check, label: 'Quizzes' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="bg-blue-50 rounded-xl p-3 text-center">
                    <Icon className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs font-medium text-blue-800">{label}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  data-testid="generate-dashboard-btn"
                  onClick={handleGenerateStudyDashboard}
                  className="rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 px-6"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Generate Study Dashboard
                </Button>
                <Button
                  data-testid="skip-dashboard-btn"
                  onClick={handleSkipStudyDashboard}
                  variant="outline"
                  className="rounded-xl"
                >
                  <SkipForward className="w-4 h-4 mr-2" />
                  Maybe Later
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                You can always generate a study dashboard later from the AI Study Builder page.
              </p>
            </div>
          </div>
        )}

        {/* Upload Form */}
        {uploadStep === 'form' && (
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
              <UploadIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
              Upload Document
            </h1>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6">
            <button
              data-testid="upload-tab-document"
              onClick={() => setUploadTab('document')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                uploadTab === 'document'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <UploadIcon className="w-4 h-4" />
              Upload Document
            </button>
            <button
              data-testid="upload-tab-handwritten"
              onClick={() => setUploadTab('handwritten')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                uploadTab === 'handwritten'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <PenTool className="w-4 h-4" />
              Handwritten
            </button>
            <button
              data-testid="upload-tab-bulk"
              onClick={() => setUploadTab('bulk')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                uploadTab === 'bulk'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Package className="w-4 h-4" />
              Bulk Upload
            </button>
          </div>

          {uploadTab === 'handwritten' ? (
            <HandwrittenUpload
              onDocumentCreated={() => navigate('/study-builder')}
              hasSubscription={false}
            />
          ) : uploadTab === 'bulk' ? (
            <BulkUpload user={user} embedded />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <Input
                data-testid="upload-title-input"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Advanced Calculus Study Notes"
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field *</label>
                {!showCustomField ? (
                  <>
                    <Select value={formData.field} onValueChange={(v) => {
                      if (v === 'custom') {
                        setShowCustomField(true);
                        setFormData({ ...formData, field: '' });
                      } else {
                        setFormData({ ...formData, field: v });
                      }
                    }}>
                      <SelectTrigger data-testid="upload-field-select" className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELDS.map(field => (
                          <SelectItem key={field} value={field}>{field}</SelectItem>
                        ))}
                        <SelectItem value="custom">+ Add Custom Field</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      data-testid="custom-field-input"
                      type="text"
                      required
                      value={customField}
                      onChange={(e) => {
                        setCustomField(e.target.value);
                        setFormData({ ...formData, field: e.target.value });
                      }}
                      placeholder="Enter custom field..."
                      className="rounded-xl"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCustomField(false);
                        setCustomField('');
                        setFormData({ ...formData, field: FIELDS[0] });
                      }}
                      className="rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Type *</label>
                {!showCustomDocType ? (
                  <>
                    <Select value={formData.document_type} onValueChange={(v) => {
                      if (v === 'custom') {
                        setShowCustomDocType(true);
                        setFormData({ ...formData, document_type: '' });
                      } else {
                        setFormData({ ...formData, document_type: v });
                      }
                    }}>
                      <SelectTrigger data-testid="upload-type-select" className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map(type => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">+ Add Custom Type</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      data-testid="custom-doc-type-input"
                      type="text"
                      required
                      value={customDocType}
                      onChange={(e) => {
                        setCustomDocType(e.target.value);
                        setFormData({ ...formData, document_type: e.target.value });
                      }}
                      placeholder="Enter custom type..."
                      className="rounded-xl"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCustomDocType(false);
                        setCustomDocType('');
                        setFormData({ ...formData, document_type: DOC_TYPES[0] });
                      }}
                      className="rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price (USD) *</label>
              <Input
                data-testid="upload-price-input"
                type="number"
                step="0.01"
                min="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="9.99"
                className="rounded-xl"
              />
              {formData.price && parseFloat(formData.price) > 0 && (
                <div className="mt-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Your earnings:</span> ${parseFloat(formData.price).toFixed(2)}
                    <span className="text-gray-500 ml-2">(You keep 100% — the 15% platform fee is added on top for the buyer)</span>
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <Textarea
                data-testid="upload-description-input"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your document, what topics it covers, and who it's for..."
                rows={4}
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview Content *</label>
              <p className="text-sm text-gray-500 mb-2">This will be visible to everyone before purchase</p>
              <Textarea
                data-testid="upload-preview-input"
                required
                value={formData.preview_content}
                onChange={(e) => setFormData({ ...formData, preview_content: e.target.value })}
                placeholder="Enter the first few paragraphs or an excerpt to preview..."
                rows={6}
                className="rounded-xl font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Content *</label>
              <p className="text-sm text-gray-500 mb-2">Only visible after purchase</p>
              
              {/* Drag and Drop Area */}
              <div
                className={`mb-4 border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileInput}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <UploadIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Drag and drop your file here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      or click to browse
                    </p>
                    <p className="text-xs text-gray-400">
                      All file types supported: Documents, Images, Videos, Audio, Archives (max 50MB)
                    </p>
                  </div>
                </label>
              </div>

              {/* Uploaded File Display */}
              {uploadedFile && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{uploadedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    className="rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <Textarea
                data-testid="upload-content-input"
                required
                value={formData.full_content}
                onChange={(e) => setFormData({ ...formData, full_content: e.target.value })}
                placeholder="Paste or type your complete document content here, or drag and drop a file above..."
                rows={12}
                className="rounded-xl font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
              <Input
                data-testid="upload-tags-input"
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="calculus, derivatives, integrals (comma-separated)"
                className="rounded-xl"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                data-testid="upload-submit-btn"
                type="submit"
                disabled={uploading}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
              <Button
                data-testid="upload-cancel-btn"
                type="button"
                onClick={() => navigate('/')}
                variant="outline"
                className="rounded-xl"
              >
                Cancel
              </Button>
            </div>
          </form>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default Upload;
